import { z } from 'zod'

const mobileRegex = /^[0-9]{1,10}$/ // Mobile should be a number of 1 to 10 digits
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/ // Basic email validation regex
const businessPhoneRegex = /^[0-9]{1,10}$/ // Business phone should not be more than 10 digits

export const distributorValidation = z.object({
  administrator: z.object({
    firstName: z.string().min(1, { message: 'First name must not be blank' }).max(30, { message: 'First name must not be longer than 30 characters' }),
    lastName: z.string().max(30, { message: 'Last name must not be longer than 30 characters' }),
    email: z.string().min(1, { message: 'Email must not be blank' }).max(50, { message: 'Email must not be longer than 50 characters' }),
    username: z.string().min(6, { message: 'Username must be at least 6 characters' }).max(16, { message: 'Username must not be longer than 16 characters' }),
  }),

  client: z.object({
    name: z.string().min(1, { message: 'Distributor name must not be blank' }).max(30, { message: 'Distributor name must not be longer than 30 characters' }),
    desp: z.string().max(255, { message: 'Description must not be longer than 255 characters' }).optional(),
    remarks: z.string().max(255, { message: 'Remarks must not be longer than 255 characters' }).optional(),
    timezone: z.string().min(1, { message: 'Timezone must not be blank' }),
    address: z.object({
      line1: z.string().min(1, { message: 'Address Line 1 must not be blank' }).max(50, { message: 'Address Line 1 must not be longer than 50 characters' }),
      line2: z.string().max(50, { message: 'Address Line 2 must not be longer than 50 characters' }),
      line3: z.string().max(50, { message: 'Address Line 3 must not be longer than 50 characters' }),
      countryId: z.string().min(1, { message: 'Country must not be blank' }),
      type: z.string().min(1, { message: 'Type must not be blank' }),
      postcode: z.string().min(1, { message: 'Post code must not be blank' }).max(12, { message: 'Post code must not be longer than 12 characters' }),
      state: z.string().min(1, { message: 'State must not be blank' }).max(50, { message: 'State must not be longer than 50 characters' }),
      city: z.string().min(1, { message: 'City must not be blank' }).max(30, { message: 'City must not be longer than 30 characters' }),
    }),
    personsInCharge: z.array(z.object({
      firstName: z.string().min(1, { message: 'First name must not be blank' }).max(50, { message: 'First name must not be longer than 50 characters' }),
      lastName: z.string().max(50, { message: 'Last name must not be longer than 50 characters' }),
      contacts: z.array(z.object({
        contactVal: z.string().min(1, { message: 'Contact must not be blank' }).max(30, { message: 'Contact must not be longer than 30 characters' }),
        contactLocalVal: z.string().optional(),
        type: z.string().min(1, { message: 'Contact type must not be blank' }),
      }).superRefine((data, ctx) => {
        // Conditional validation based on the type
        if (data.type === 'MOBILE' && ((data.contactLocalVal && !mobileRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid mobile number. It should not more then 10 digits.',
            path: ['contactVal'],
          })
        }
        else if (data.type === 'EMAIL' && !emailRegex.test(data.contactVal)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid email address.',
            path: ['contactVal'],
          })
        }
        else if (data.type === 'BUSINESS_PHONE' && ((data.contactLocalVal && !businessPhoneRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid business phone number. It should not more then 10 digits.',
            path: ['contactVal'],
          })
        }
      })).optional(),
    })).optional(),
    contacts: z.array(z.object({
      contactVal: z.string().min(1, { message: 'Contact must not be blank' }).max(30, { message: 'Contact must not be longer than 30 characters' }),
      contactLocalVal: z.string().optional(),
      type: z.string().min(1, { message: 'Contact type must not be blank' }),
    }).superRefine((data, ctx) => {
      // Conditional validation based on the type
      if (data.type === 'MOBILE' && ((data.contactLocalVal && !mobileRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid mobile number. It should not more then 10 digits.',
          path: ['contactVal'],
        })
      }
      else if (data.type === 'EMAIL' && !emailRegex.test(data.contactVal)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid email address.',
          path: ['contactVal'],
        })
      }
      else if (data.type === 'BUSINESS_PHONE' && ((data.contactLocalVal && !businessPhoneRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid business phone number. It should not more then 10 digits.',
          path: ['contactVal'],
        })
      }
    })).optional(),
  }),
})

export const updateDistributorValidation = z.object({
  name: z.string().min(1, { message: 'Distributor name must not be blank' }).max(30, { message: 'Distributor name must not be longer than 30 characters' }),
  desp: z.string().max(255, { message: 'Description must not be longer than 255 characters' }).optional(),
  remarks: z.string().max(255, { message: 'Remarks must not be longer than 255 characters' }).optional(),
  timezone: z.string().min(1, { message: 'Timezone must not be blank' }),
  address: z.object({
    line1: z.string().min(1, { message: 'Address Line 1 must not be blank' }).max(50, { message: 'Address Line 1 must not be longer than 50 characters' }),
    line2: z.string().max(50, { message: 'Address Line 2 must not be longer than 50 characters' }),
    line3: z.string().max(50, { message: 'Address Line 3 must not be longer than 50 characters' }),
    countryId: z.string().min(1, { message: 'Country must not be blank' }),
    type: z.string().min(1, { message: 'Type must not be blank' }),
    postcode: z.string().min(1, { message: 'Post code must not be blank' }),
    state: z.string().min(1, { message: 'State must not be blank' }).max(50, { message: 'State must not be longer than 50 characters' }),
    city: z.string().min(1, { message: 'City must not be blank' }).max(30, { message: 'City must not be longer than 30 characters' }),
  }),
  personsInCharge: z.array(z.object({
    firstName: z.string().min(1, { message: 'First name must not be blank' }).max(50, { message: 'First name must not be longer than 50 characters' }),
    lastName: z.string().max(50, { message: 'Last name must not be longer than 50 characters' }),
    contacts: z.array(z.object({
      contactVal: z.string().min(1, { message: 'Contact must not be blank' }).max(30, { message: 'Contact must not be longer than 30 characters' }),
      contactLocalVal: z.string().optional(),
      type: z.string().min(1, { message: 'Contact type must not be blank' }),
    }).superRefine((data, ctx) => {
      // Conditional validation based on the type
      if (data.type === 'MOBILE' && ((data.contactLocalVal && !mobileRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid mobile number. It should not more then 10 digits.',
          path: ['contactVal'],
        })
      }
      else if (data.type === 'EMAIL' && !emailRegex.test(data.contactVal)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid email address.',
          path: ['contactVal'],
        })
      }
      else if (data.type === 'BUSINESS_PHONE' && ((data.contactLocalVal && !businessPhoneRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid business phone number. It should not more then 10 digits.',
          path: ['contactVal'],
        })
      }
    })).optional(),
  })).optional(),
  contacts: z.array(z.object({
    contactVal: z.string().min(1, { message: 'Contact must not be blank' }).max(30, { message: 'Contact must not be longer than 30 characters' }),
    contactLocalVal: z.string().optional(),
    type: z.string().min(1, { message: 'Contact type must not be blank' }),
  }).superRefine((data, ctx) => {
    // Conditional validation based on the type
    if (data.type === 'MOBILE' && ((data.contactLocalVal && !mobileRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid mobile number. It should not more then 10 digits.',
        path: ['contactVal'],
      })
    }
    else if (data.type === 'EMAIL' && !emailRegex.test(data.contactVal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid email address.',
        path: ['contactVal'],
      })
    }
    else if (data.type === 'BUSINESS_PHONE' && ((data.contactLocalVal && !businessPhoneRegex.test(data.contactLocalVal)) ?? (!data.contactVal && data.contactLocalVal === undefined))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid business phone number. It should not more then 10 digits.',
        path: ['contactVal'],
      })
    }
  })).optional(),
})

export type DistributorValidation = z.infer<typeof distributorValidation>
